import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { AsyncStatus } from '../types';

/**
 * Login page — email + password sign-in via Supabase Auth.
 * On success the AuthProvider's onAuthStateChange fires and
 * ProtectedRoute redirects the user to the dashboard.
 */
function LoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
    } else {
      setStatus('success');
      navigate('/', { replace: true });
    }
  };

  return (
    <main className="page-container flex flex-col items-center justify-center min-h-screen animate-fade-in">
      {/* Logo */}
      <div className="mb-6 w-16 h-16 rounded-2xl bg-brand-500 flex items-center justify-center shadow-lg">
        <span className="text-3xl" role="img" aria-label="paw">🐾</span>
      </div>

      <h1 className="font-display text-3xl font-bold text-white mb-1">
        Welcome back
      </h1>
      <p className="text-white/50 text-sm mb-8">Sign in to your PetNoter account</p>

      {/* Card */}
      <div className="glass-card w-full max-w-sm p-6 animate-slide-up">
        <form id="login-form" onSubmit={handleSubmit} noValidate className="space-y-4">

          {/* Email */}
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-white/80 mb-1">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-field"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-white/80 mb-1">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-field"
            />
          </div>

          {/* Error message */}
          {status === 'error' && (
            <div
              role="alert"
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3"
            >
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            id="login-submit"
            type="submit"
            disabled={status === 'loading'}
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-white/30 text-xs">OR</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <p className="text-center text-sm text-white/50">
          Don't have an account?{' '}
          <Link
            to="/register"
            id="go-to-register"
            className="text-brand-400 font-semibold hover:text-brand-300 transition-colors"
          >
            Create one
          </Link>
        </p>
      </div>
    </main>
  );
}

export default LoginPage;
